import React, { useEffect, useRef, useState } from "react";

/**
 * Tween an integer value from its previous render to the next. Only used in
 * the Dashboard hero strip today, where year / portfolio filter changes cause
 * the category counts to shift — a static snap makes the strip feel like it
 * missed the click; a 450ms ease-out makes the change deliberate.
 *
 * Design rules:
 *   - Skip tween on first render. We don't want users watching a "0 → N" wipe
 *     on initial page load.
 *   - Respect `prefers-reduced-motion`. Snap to end value, no RAF loop.
 *   - Only tween numbers — strings, plurals, and sublabels update instantly.
 *   - Stable across React strict-mode double-invocation by capturing the
 *     "previous" value in a ref.
 */

const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
};

interface AnimatedNumberProps {
  value: number;
  durationMs?: number;
  format?: (n: number) => string;
}

export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  durationMs = 450,
  format = (n) => Math.round(n).toLocaleString(),
}) => {
  const [displayed, setDisplayed] = useState(value);
  const prevRef = useRef(value);
  const rafRef = useRef<number | null>(null);
  const firstRenderRef = useRef(true);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      prevRef.current = value;
      setDisplayed(value);
      return undefined;
    }
    if (prefersReducedMotion()) {
      prevRef.current = value;
      setDisplayed(value);
      return undefined;
    }
    const from = prevRef.current;
    const to = value;
    if (from === to) return undefined;

    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = easeOutCubic(t);
      setDisplayed(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevRef.current = to;
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Capture the latest target so the next tween starts from the real
      // current value, not from a stale mid-animation value.
      prevRef.current = value;
    };
  }, [value, durationMs]);

  return <>{format(displayed)}</>;
};

export default AnimatedNumber;
